import type {
  AccessRequirement,
  AccessRequirementCtaKind,
  AccessRequirementKey,
  AccessRequirementParams,
  AccessRequirementState,
  AccessState,
  IfStatusType,
  ResolveAccessStateParams,
  SourceLoadStatus,
  VerificationStatusType,
} from './access-control.types';
import { ACCESS_REQUIREMENT_KEYS, getAccessRequirementDefinition } from './access-control.types';

export type KybOnboardingStateKey =
  'unstarted-kyb' | 'action-needed-kyb' | 'pending-kyb' | 'finished-kyb';

export const isResolvedSourceStatus = (status: SourceLoadStatus) => {
  return status === 'loaded' || status === 'failed';
};

export const resolveVerificationRequirementState = (
  status?: VerificationStatusType | null,
  sourceStatus: SourceLoadStatus = 'idle',
): AccessRequirementState => {
  if (sourceStatus === 'failed') {
    return 'required';
  }

  if (status === 'GREEN') {
    return 'satisfied';
  }

  if (status === 'YELLOW') {
    return 'pending';
  }

  return 'required';
};

const resolveVerificationRequirementCtaKind = (
  status?: VerificationStatusType | null,
  sourceStatus: SourceLoadStatus = 'idle',
): AccessRequirementCtaKind => {
  if (sourceStatus === 'failed') {
    return 'refresh';
  }

  if (status === 'GREEN') {
    return 'none';
  }

  if (status === 'YELLOW') {
    return 'refresh';
  }

  if (status === 'RETRY' || status === 'RED') {
    return 'resume';
  }

  return 'start';
};

const resolveVerificationRequirementStatus = (
  status?: VerificationStatusType | null,
  sourceStatus: SourceLoadStatus = 'idle',
) => {
  if (sourceStatus === 'failed') {
    return 'ERROR' as const;
  }

  return status ?? 'NOT_STARTED';
};

export const resolveVerificationRequirement = (
  key: 'kyc' | 'kyb',
  status?: VerificationStatusType | null,
  sourceStatus: SourceLoadStatus = 'idle',
): AccessRequirement => {
  const state = resolveVerificationRequirementState(status, sourceStatus);

  return {
    key,
    state,
    status: resolveVerificationRequirementStatus(status, sourceStatus),
    sourceStatus,
    isResolved: state === 'satisfied',
    isBlocking: state === 'required',
    ctaKind: resolveVerificationRequirementCtaKind(status, sourceStatus),
    ctaEnabled: state !== 'satisfied',
  };
};

export const resolveIfRequirement = (isIfSatisfied: boolean): AccessRequirement => {
  const status: IfStatusType = isIfSatisfied ? 'READY' : 'NOT_READY';

  return {
    key: 'if',
    state: isIfSatisfied ? 'satisfied' : 'pending',
    status,
    sourceStatus: 'loaded',
    isResolved: isIfSatisfied,
    isBlocking: false,
    ctaKind: isIfSatisfied ? 'none' : 'wait',
    ctaEnabled: false,
  };
};

export const resolveEmailRequirement = (isEmailConfirmed: boolean): AccessRequirement => {
  const status: IfStatusType = isEmailConfirmed ? 'READY' : 'NOT_READY';

  return {
    key: 'email',
    state: isEmailConfirmed ? 'satisfied' : 'required',
    status,
    sourceStatus: 'loaded',
    isResolved: isEmailConfirmed,
    isBlocking: !isEmailConfirmed,
    ctaKind: 'wait',
    ctaEnabled: false,
  };
};

export const resolvePhoneRequirement = (
  isPhoneVerified: boolean,
  sourceStatus: SourceLoadStatus,
): AccessRequirement => {
  if (sourceStatus === 'failed') {
    return {
      key: 'phone',
      state: 'required',
      status: 'ERROR',
      sourceStatus,
      isResolved: false,
      isBlocking: true,
      ctaKind: 'wait',
      ctaEnabled: false,
    };
  }

  const status: IfStatusType = isPhoneVerified ? 'READY' : 'NOT_READY';

  return {
    key: 'phone',
    state: isPhoneVerified ? 'satisfied' : 'required',
    status,
    sourceStatus,
    isResolved: isPhoneVerified,
    isBlocking: !isPhoneVerified,
    ctaKind: isPhoneVerified ? 'none' : 'wait',
    ctaEnabled: false,
  };
};

export const resolveAccessRequirements = ({
  isEmailConfirmed,
  isPhoneVerified,
  phoneSourceStatus,
  kycStatus,
  kycSourceStatus,
  kybStatus,
  kybSourceStatus,
  isIfSatisfied,
}: AccessRequirementParams): AccessRequirement[] => {
  const requirementByKey: Record<AccessRequirementKey, AccessRequirement> = {
    email: resolveEmailRequirement(isEmailConfirmed),
    phone: resolvePhoneRequirement(isPhoneVerified, phoneSourceStatus),
    kyb: resolveVerificationRequirement('kyb', kybStatus, kybSourceStatus),
    kyc: resolveVerificationRequirement('kyc', kycStatus, kycSourceStatus),
    if: resolveIfRequirement(isIfSatisfied),
  };

  return ACCESS_REQUIREMENT_KEYS.map((key) => requirementByKey[key]);
};

export const getAccessRequirement = (
  requirements: AccessRequirement[],
  key: AccessRequirementKey,
) => {
  return requirements.find((requirement) => requirement.key === key) ?? null;
};

export const resolveKybOnboardingStateKey = ({
  accessState,
  requirements,
}: {
  accessState: AccessState;
  requirements: AccessRequirement[];
}): KybOnboardingStateKey | null => {
  if (accessState === 'unauthenticated') {
    return null;
  }

  const kybRequirement = getAccessRequirement(requirements, 'kyb');

  if (!kybRequirement) {
    return null;
  }

  if (accessState === 'onboarded') {
    return 'finished-kyb';
  }

  if (
    kybRequirement.status === 'RED' ||
    kybRequirement.status === 'RETRY' ||
    kybRequirement.sourceStatus === 'failed'
  ) {
    return 'action-needed-kyb';
  }

  if (
    kybRequirement.sourceStatus === 'idle' ||
    kybRequirement.sourceStatus === 'loading' ||
    kybRequirement.state === 'pending'
  ) {
    return 'pending-kyb';
  }

  if (kybRequirement.state === 'required') {
    return 'unstarted-kyb';
  }

  return null;
};

export const resolveKybOnboardingKnobValue = (stateKey: KybOnboardingStateKey | null) => {
  if (stateKey === 'unstarted-kyb') {
    return 20;
  }

  if (stateKey === 'action-needed-kyb') {
    return 20;
  }

  if (stateKey === 'pending-kyb') {
    return 60;
  }

  if (stateKey === 'finished-kyb') {
    return 100;
  }

  return null;
};

export const resolveAccessState = ({
  hasJwtToken,
  isAccessResolved,
  requirements,
}: ResolveAccessStateParams): AccessState => {
  if (!hasJwtToken) {
    return 'unauthenticated';
  }

  if (!isAccessResolved) {
    return 'resolving';
  }

  const requirementKeys = new Set<AccessRequirementKey>(
    requirements.map((requirement) => requirement.key),
  );
  const hasAllRequirements = ACCESS_REQUIREMENT_KEYS.every((key) => requirementKeys.has(key));

  if (!hasAllRequirements) {
    return 'blocked';
  }

  if (requirements.some((requirement) => requirement.state === 'required')) {
    return 'blocked';
  }

  if (requirements.some((requirement) => requirement.state === 'pending')) {
    return 'pending';
  }

  return 'onboarded';
};

export const getAccessRequirementStepLabel = (key: AccessRequirementKey) => {
  return getAccessRequirementDefinition(key)?.stepLabel ?? key;
};

export const getAccessRequirementKnobValue = (key: AccessRequirementKey) => {
  return getAccessRequirementDefinition(key)?.totalBalanceKnobValue ?? 0;
};
